import { Restaurant, AttachMoney, WarningAmber, FavoriteBorder, Assessment } from '@mui/icons-material';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { InsightTileGroup } from '../../../components/shared/tiles/InsightTileGroup';
import AllergenChips from './RecipeV2AllergenChips';
import DietaryAttributeChips from './DietaryAttributeChips';
import { RecipeV2Detail } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

/**
 * Builds the six summary insight tiles for a recipe. Extracted as a plain
 * function (not a hook) so callers that already hold a `t` function (e.g. a
 * parent page composing its own headline for DetailPageTemplate) can reuse
 * the same tile definitions without violating rules-of-hooks.
 */
export const getRecipeSummaryInsights = (
  recipe: RecipeV2Detail,
  currencyCode: string | undefined,
  t: TFunction,
) => {
  const primaryYield = recipe.yieldPortions;
  const allergens = recipe.allergenSummary ?? [];
  const dietary = recipe.dietaryAttributes ?? [];
  const nutrition = recipe.nutrition ?? [];

  return [
    <InsightTile key="yield"
      label={t('yield')}
      value={primaryYield ? `${primaryYield} portions` : '—'}
      description={recipe.portionQuantity && recipe.portionUom
        ? `${recipe.portionQuantity} ${recipe.portionUom} each`
        : recipe.primaryCategory || '—'}
      icon={<Restaurant />}
      variant="primary" />,
    <InsightTile key="cost"
      label={t('cost')}
      value={recipe.costIsComplete && recipe.totalCost != null ? applyRoundAndFormat(recipe.totalCost, currencyCode) : '—'}
      description={recipe.costIsComplete ? t('build.production') : t('costing.incompleteShort')}
      icon={<AttachMoney />}
      variant="success" />,
    <InsightTile key="cost-per-portion"
      label={t('costPerPortion')}
      value={recipe.costIsComplete && recipe.costPerPortion != null ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4) : '—'}
      description={recipe.costIsComplete ? t('costing.perPortion', 'Per portion') : t('costing.incompleteShort')}
      icon={<AttachMoney />}
      variant="warning" />,
    <InsightTile key="allergens"
      label={t('allergensLabel')}
      value={allergens.length}
      description={allergens.length ? <AllergenChips allergens={allergens}
        max={3} /> : t('allergens.noAllergens')}
      icon={<WarningAmber />}
      variant="error" />,
    <InsightTile key="dietary"
      label={t('tabs.dietary')}
      value={dietary.length}
      description={dietary.length ? <DietaryAttributeChips attributes={dietary}
        max={3} /> : t('dietary.noAttributes')}
      icon={<FavoriteBorder />}
      variant="success" />,
    <InsightTile key="nutrition"
      label={t('tabs.nutrition')}
      value={nutrition.length}
      description={nutrition.length ? t('nutrition.recipeDerived', 'Recipe-derived') : t('nutrition.unavailable', 'Not available')}
      icon={<Assessment />}
      variant="info" />,
  ] as const;
};

const RecipeSummaryTiles = ({ recipe, currencyCode }: { recipe: RecipeV2Detail; currencyCode?: string }) => {
  const { t } = useTranslation('recipeV2');

  return (
    <InsightTileGroup
      layout="six"
      insights={getRecipeSummaryInsights(recipe, currencyCode, t)}
    />
  );
};

export default RecipeSummaryTiles;
